import { transformFlowToStages } from '@/features/boe-task/-query/transformers';
import type { ApprovalTaskConfigFlow } from '@/features/boe-task/components/ApprovalTaskConfig/ApprovalTaskConfig';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import type { OwnerProduct } from '../PackagePatternConfig/-types';
import type { PackagePatternDraftConfig } from '../types/pattern-draft-config';
import { applyPackagePatternDraftToEditingContent, isApprovalDraftFlowValid, toPackagePatternDraftConfig, type DraftPatternSettingJson } from '../utils/draft-codec';
type DraftPathParams = {
  ownerProduct: OwnerProduct;
  id: string;
  patternId: string;
};

/**
 * 各タスクノードの解決済み表示名（BE が resolve-on-read で返す・非永続）。
 * 値は公開後 getConfig の steps[].tasks[].name と同一（DC-8208）。
 */
export type PackagePatternDraftTaskDisplayNames = {
  /** フリーフォーム submit タスクの表示名。解決不可なら null。 */
  freeFormSubmit: string | null;
  /** 承認タスクの表示名。承認ノードを持つパターンのときのみ非 null。 */
  approval: string | null;
};
type DraftGetResponse = {
  editingContent: DraftPatternSettingJson;
  status: string;
  taskDisplayNames?: PackagePatternDraftTaskDisplayNames;
};

/**
 * draft GET/PUT/publish/archive は OpenAPI 未反映のため型安全な apiClient パスは使わない。
 */
const draftApiClient = apiClient as unknown as {
  GET: (path: '/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/draft', opts: {
    params: {
      path: DraftPathParams;
    };
  }) => Promise<Parameters<typeof getDataOrThrowApiError>[0]>;
  PUT: (path: '/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/draft', opts: {
    params: {
      path: DraftPathParams;
    };
    body: {
      editingContent: Record<string, never>;
    };
  }) => Promise<Parameters<typeof getDataOrThrowApiError>[0]>;
  POST: (path: '/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/draft/publish' | '/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/draft/archive', opts: {
    params: {
      path: DraftPathParams;
    };
  }) => Promise<Parameters<typeof getDataOrThrowApiError>[0]>;
};

/** パターン下書きを新規作成する。 */
export async function createPackagePatternDraft(ownerProduct: OwnerProduct, packageId: string, values: {
  name: string;
  description?: string;
}): Promise<{
  operationPackagePatternId: string;
  name: string;
  description?: string;
  anchorDateLabel: string;
}[]> {
  const res = await apiClient.POST('/v2/boe/packages/{ownerProduct}/{id}/patterns', {
    params: {
      path: {
        ownerProduct,
        id: packageId
      }
    },
    body: values
  });
  const draft = getDataOrThrowApiError(res) as unknown as {
    operationPackagePatternId: string;
  };
  return [{
    operationPackagePatternId: draft.operationPackagePatternId,
    name: values.name,
    description: values.description,
    anchorDateLabel: ''
  }];
}

/** パターン下書きを取得する。 */
export async function getPackagePatternDraft(ownerProduct: OwnerProduct, packageId: string, patternId: string, options?: {
  packageDisplayName?: string;
}): Promise<{
  config: PackagePatternDraftConfig;
  editingContent: DraftPatternSettingJson;
  taskDisplayNames: PackagePatternDraftTaskDisplayNames;
}> {
  const res = await draftApiClient.GET('/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/draft', {
    params: {
      path: {
        ownerProduct,
        id: packageId,
        patternId
      }
    }
  });
  const data = getDataOrThrowApiError(res) as unknown as DraftGetResponse;
  const {
    editingContent,
    status
  } = data;
  return {
    config: toPackagePatternDraftConfig({
      patternId,
      editingContent,
      status,
      packageDisplayName: options?.packageDisplayName
    }),
    editingContent,
    // 旧 BE 互換: 未提供なら null 埋め（FE 側で i18n フォールバック）。
    taskDisplayNames: data.taskDisplayNames ?? {
      freeFormSubmit: null,
      approval: null
    }
  };
}

/** パターン下書きを保存する。publish=true のときは公開まで行う。 */
export async function savePackagePatternDraft(ownerProduct: OwnerProduct, packageId: string, patternId: string, args: {
  config: Pick<PackagePatternDraftConfig, 'name' | 'description' | 'questions'>;
  baseEditingContent: DraftPatternSettingJson;
  approvalFlow?: ApprovalTaskConfigFlow;
}, options?: {
  publish?: boolean;
}): Promise<void> {
  const editingContent = applyPackagePatternDraftToEditingContent({
    base: args.baseEditingContent,
    config: args.config,
    approvalFlow: args.approvalFlow
  });
  const saveRes = await draftApiClient.PUT('/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/draft', {
    params: {
      path: {
        ownerProduct,
        id: packageId,
        patternId
      }
    },
    body: {
      editingContent: editingContent as unknown as Record<string, never>
    }
  });
  getDataOrThrowApiError(saveRes);
  if (options?.publish) {
    const publishRes = await draftApiClient.POST('/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/draft/publish', {
      params: {
        path: {
          ownerProduct,
          id: packageId,
          patternId
        }
      }
    });
    getDataOrThrowApiError(publishRes);
    if (args.approvalFlow && isApprovalDraftFlowValid(args.approvalFlow)) {
      await savePublishedApprovalFlow(ownerProduct, patternId, args.approvalFlow);
    }
  }
}

/**
 * 公開済みパターンの承認フローを保存する。
 * draft の approvalStages は publish 時にエンティティ化されないため、公開後に getConfig → approval-flow PUT する。
 */
async function savePublishedApprovalFlow(ownerProduct: OwnerProduct, patternId: string, flow: ApprovalTaskConfigFlow): Promise<void> {
  const detailRes = await apiClient.GET('/v2/boe/package/{ownerProduct}/configs/{packagePatternId}', {
    params: {
      path: {
        ownerProduct,
        packagePatternId: patternId
      }
    }
  });
  const detail = getDataOrThrowApiError(detailRes);
  const approvalTask = detail.steps.flatMap(step => step.tasks).find(task => task.taskType === 'Approval');
  if (!approvalTask?.id) return;
  const stages = transformFlowToStages(flow).map(stage => ({
    ...stage,
    designationMode: stage.designationMode ?? 'none'
  }));
  if (stages.length === 0) return;
  const saveRes = await apiClient.PUT('/v2/boe/package/{ownerProduct}/configs/{packagePatternId}/approval-task-catalogs/{approvalTaskCatalogId}/approval-flow', {
    params: {
      path: {
        ownerProduct,
        packagePatternId: patternId,
        approvalTaskCatalogId: approvalTask.id
      }
    },
    body: {
      stages
    }
  });
  getDataOrThrowApiError(saveRes);
}

/** 公開済みパターンをアーカイブ（退役）する。 */
export async function archivePackagePatternDraft(ownerProduct: OwnerProduct, packageId: string, patternId: string): Promise<void> {
  const res = await draftApiClient.POST('/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/draft/archive', {
    params: {
      path: {
        ownerProduct,
        id: packageId,
        patternId
      }
    }
  });
  getDataOrThrowApiError(res);
}